export type AddressFields = {
  property_address?: string | null;
  property_apt_unit?: string | null;
  property_city?: string | null;
  property_state?: string | null;
  property_zip_code?: string | null;
};

/**
 * Formats address fields into a single displayable string
 * Example: "123 Main St, Apt 5B, New York, NY 10001"
 */
export const formatFullAddress = (address: AddressFields | null | undefined): string => {
  if (!address) return '';

  const parts: string[] = [];

  // Street address with apt/unit
  if (address.property_address) {
    let streetPart = address.property_address;
    if (address.property_apt_unit) {
      streetPart += `, ${address.property_apt_unit}`;
    }
    parts.push(streetPart);
  }

  // City, State ZIP
  const cityStateZip: string[] = [];
  if (address.property_city) {
    cityStateZip.push(address.property_city);
  }
  if (address.property_state) {
    cityStateZip.push(address.property_state);
  }
  if (cityStateZip.length > 0) {
    let locationPart = cityStateZip.join(', ');
    if (address.property_zip_code) {
      locationPart += ` ${address.property_zip_code}`;
    }
    parts.push(locationPart);
  } else if (address.property_zip_code) {
    parts.push(address.property_zip_code);
  }

  return parts.join(', ');
};

/**
 * Checks if any address field has a value
 */
export const hasAddress = (address: AddressFields | null | undefined): boolean => {
  if (!address) return false;
  return !!(
    address.property_address ||
    address.property_apt_unit ||
    address.property_city ||
    address.property_state ||
    address.property_zip_code
  );
};

export type ZipCodeLookupResult = {
  city: string;
  state: string;
  stateAbbr: string;
};

/**
 * Looks up city and state from a US zip code using Zippopotam.us API
 * Returns null if zip code is invalid or not found
 */
export const lookupZipCode = async (zipCode: string): Promise<ZipCodeLookupResult | null> => {
  // Validate zip code format (5 digits)
  if (!/^\d{5}$/.test(zipCode)) {
    return null;
  }

  try {
    const response = await fetch(`https://api.zippopotam.us/us/${zipCode}`);
    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (data.places && data.places.length > 0) {
      const place = data.places[0];
      return {
        city: place['place name'],
        state: place.state,
        stateAbbr: place['state abbreviation'],
      };
    }
    return null;
  } catch {
    return null;
  }
};
